import type React from 'react';
import { useEffect, useState } from 'react';
import { isFeatureEnabled } from '../utils/features';
import type { MagneticWallItemSize } from './MagneticWallItem';
import { usePostLayoutSize } from './PostLayoutProvider';
import { usePostTOC } from './PostTOCContext';

const EXPANDED_MIN_WIDTH = 246;

const useIsExpanded = (
  element: HTMLElement | null | undefined,
  skip?: boolean,
) => {
  const [isExpanded, setIsExpanded] = useState(
    () => (element?.offsetWidth ?? 0) >= EXPANDED_MIN_WIDTH,
  );

  useEffect(() => {
    if (skip) return undefined;
    if (!element) return undefined;
    setIsExpanded((element?.offsetWidth ?? 0) >= EXPANDED_MIN_WIDTH);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]?.contentRect;
      if (entry) {
        setIsExpanded(entry.width >= EXPANDED_MIN_WIDTH);
      }
    });
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [element, skip]);

  return isExpanded;
};

function useMagneticWallItemSize(
  containerRef: React.RefObject<HTMLElement>,
): MagneticWallItemSize {
  const postLayoutSize = usePostLayoutSize();
  const { isTOCOpen } = usePostTOC();

  const isExpanded = useIsExpanded(
    containerRef.current,
    !isFeatureEnabled('new_post_layout'),
  );

  if (isFeatureEnabled('new_post_layout')) {
    return isExpanded ? 'full' : 'compact';
  }

  switch (postLayoutSize) {
    case 'wide':
      return isTOCOpen ? 'compact' : 'full';
    case 'xWide':
      return 'full';

    default:
      return 'compact';
  }
}

export default useMagneticWallItemSize;
